/**
 * Une feuille d'entraînement, telle qu'elle se lit à l'écran.
 *
 * Depuis le 25 septembre 2026 une feuille traverse plusieurs chapitres (Hedi,
 * *« Une feuille, plusieurs chapitres »*), si bien que le cahier et le chapitre
 * ne sont plus en tête mais **sur chaque ligne**. L'écran doit donc les
 * regrouper — et c'est la seule chose qui vit ici, hors React, parce qu'un
 * regroupement se lit sans DOM et se teste sans navigateur.
 *
 * Il vit dans `ui/` parce que **trois coquilles** le lisent — la V2, l'écran de
 * validation et la coquille enfant, qui reçoit la feuille close — et qu'aucune
 * des trois n'a le droit d'importer les deux autres. Trois découpages de la
 * même liste finiraient par ne plus couper aux mêmes endroits, et l'un des
 * trois est ce que l'enfant a sous les yeux.
 *
 * **On coupe où le chapitre change, on ne trie jamais.** Le serveur a déjà fixé
 * l'ordre — chapitres dans l'ordre où le parent les a ouverts, ordre du livre à
 * l'intérieur — et c'est celui du PDF imprimé. Retrier ici donnerait deux
 * ordres qui divergent, dont l'un est sur la table de l'enfant.
 */

import type { LigneDEntrainementOut } from "@/api/schema";
import { enClair } from "@/ui/dates";

export type BlocDeFeuille = {
  chapitre: string;
  titre: string;
  ouvrageTitre: string;
  matiere: string;
  matiereLibelle: string | undefined;
  lignes: LigneDEntrainementOut[];
};

export function parChapitre(lignes: readonly LigneDEntrainementOut[]): BlocDeFeuille[] {
  const blocs: BlocDeFeuille[] = [];
  for (const ligne of lignes) {
    const dernier = blocs[blocs.length - 1];
    const memeBloc =
      dernier &&
      dernier.chapitre === ligne.chapitre &&
      dernier.ouvrageTitre === ligne.ouvrage_titre;
    if (dernier && memeBloc) {
      dernier.lignes.push(ligne);
      continue;
    }
    blocs.push({
      chapitre: ligne.chapitre,
      titre: ligne.chapitre_titre,
      ouvrageTitre: ligne.ouvrage_titre,
      matiere: ligne.matiere,
      matiereLibelle: ligne.matiere_libelle ?? undefined,
      lignes: [ligne],
    });
  }
  return blocs;
}

/**
 * Ce qu'une feuille attend de son lecteur, en une phrase.
 *
 * Trois états et non deux : une feuille ouverte n'est pas « en retard », elle
 * n'est **pas encore partie** — l'enfant ne la voit pas. Les confondre ferait
 * chercher chez l'enfant ce qui attend chez le parent.
 */
export function etatDeLaFeuille(feuille: {
  closed_at?: string | null;
  lignes?: readonly LigneDEntrainementOut[] | null;
}): "ouverte" | "attend" | "relue" {
  if (!feuille.closed_at) return "ouverte";
  const lignes = feuille.lignes ?? [];
  return lignes.every((ligne) => ligne.verdict) ? "relue" : "attend";
}

/**
 * Qui a composé la feuille (Hedi, 5 octobre 2026).
 *
 * Un élève de lycée compose désormais ses propres entraînements, et ses
 * feuilles sont **partagées avec les parents** : elles arrivent dans la même
 * liste que celles du parent, et dans la même file « à juger ». Ce qui les
 * distingue tient en un champ que le serveur pose (`origine`), et cette
 * fonction est la seule à le lire — deux lectures finiraient par ne pas être
 * d'accord sur une feuille sans origine, que le serveur d'avant le 5 octobre
 * envoyait et qui est une feuille de parent.
 */
export function composeeParLEleve(feuille: { origine?: string | null }): boolean {
  return feuille.origine === "eleve";
}

/** « Composé par Mehdi », ou rien pour une feuille du parent. */
export function mentionDOrigine(
  feuille: { origine?: string | null },
  prenom: string | undefined,
): string | null {
  if (!composeeParLEleve(feuille)) return null;
  return prenom ? `Composé par ${prenom}` : "Composé par l'enfant";
}

/**
 * Le parent peut-il ajouter, retirer, clore ?
 *
 * Seulement sur **sa** feuille ouverte. Une feuille ouverte de l'élève est en
 * cours chez lui : c'est lui qui la remplit et lui qui la clôt quand il veut
 * lire la correction. Un bouton « Clore » chez le parent lui montrerait les
 * corrigés à un moment qu'il n'a pas choisi ; un « Retirer » ferait disparaître
 * sous ses yeux un exercice qu'il est peut-être en train de faire. Le serveur
 * refuse aussi ces gestes — l'écran ne les propose pas, ce qui vaut mieux
 * qu'un refus après coup.
 */
export function leParentPeutAgir(feuille: {
  origine?: string | null;
  closed_at?: string | null;
}): boolean {
  return !feuille.closed_at && !composeeParLEleve(feuille);
}

/**
 * Le titre d'une feuille dans la file « À valider ».
 *
 * La file reçoit depuis le 5 octobre 2026 les feuilles de l'élève, **ouvertes
 * comprises** : il la travaille avant de la clore, et une photo envoyée mérite
 * d'être relue sans attendre (`parascolaire.a_juger`). « Feuille close le … »
 * y serait faux deux fois — elle n'est peut-être pas close, et ce n'est pas le
 * parent qui l'a composée.
 */
export function titreAValider(
  feuille: { origine?: string | null; closed_at?: string | null; composed_at: string },
  prenom: string | undefined,
): string {
  const mention = mentionDOrigine(feuille, prenom);
  if (!mention) return `Feuille close le ${enClair(feuille.closed_at ?? feuille.composed_at)}`;
  return feuille.closed_at
    ? `${mention} — close le ${enClair(feuille.closed_at)}`
    : `${mention} — encore en cours`;
}

/**
 * Ce qu'il faut savoir avant de juger une feuille de l'élève.
 *
 * Il voit **tous les corrigés** en la clôturant — l'exception à la règle 2
 * écrite le 5 octobre 2026. Une copie photographiée après ne prouverait donc
 * rien : le serveur n'accepte de photo qu'avant la clôture, et refuse un
 * verdict noté sur une ligne sans photo (« pas fait » reste possible). Le
 * parent qui l'ignore cliquerait « juste » et lirait un refus.
 */
export function avantDeJugerLaFeuilleDeLEleve(
  feuille: { origine?: string | null; closed_at?: string | null },
  prenom: string | undefined,
): string | null {
  if (!composeeParLEleve(feuille)) return null;
  const qui = prenom ?? "L'enfant";
  const corriges = feuille.closed_at
    ? `${qui} a vu tous les corrigés en la clôturant.`
    : `${qui} verra tous les corrigés en la clôturant.`;
  return `${corriges} Seul un exercice photographié avant la clôture se juge sur la copie ; sans photo, il ne se juge que « pas fait ».`;
}
